<template>
  <v-container class="pa-0 ma-0 d-flex flex-column gap-2 report-wrapper">
    <!-- TITLE -->
    <v-card-text class="pa-0 ma-0 text-body-1 title"
      >{{ $t("Payroll summary") }}/ {{ $t("Employee salaries and benefits") }}
      {{ salaryMonth || "-" }} / {{ salaryYear || "-" }}</v-card-text
    >
    <v-card-text class="pa-0 ma-0 text-body-2 subtitle">
      {{ $t("workshopCode") }}: {{ workshopCode || "-" }}
    </v-card-text>
    <!-- DESCRIPTION -->
    <v-card-text class="pa-0 ma-0 text-body-2 desc">
      {{
        $t(
          "This form relates to the monthly payroll/salaries and benefits of the workshop’s employees, and a summary of its status is presented below"
        )
      }}
    </v-card-text>
    <div class="table-scroll-x">
      <!-- SUMMARY TABLE -->
      <table class="summary-table">
        <tbody>
          <tr
            v-for="(row, idx) in summaryRows"
            :key="idx"
            :class="{ 'summary-total-row': row.isTotal }"
          >
            <th scope="row" class="label-cell">{{ row.label }}</th>
            <td class="amount-cell">{{ formatSummaryAmount(row.amount) }}</td>
            <td class="currency-cell">{{ currencyLabel || "-" }}</td>
          </tr>
          <tr v-if="!summaryRows.length">
            <td colspan="3">-</td>
          </tr>
        </tbody>
      </table>
    </div>
    <!-- CONFIRMATION TEXT -->
    <v-card-text class="pa-0 ma-0 text-body-2 confirm">
      {{
        $t(
          "Furthermore, it is hereby undertaken that the information stored on the diskette exactly corresponds to the contents stated above."
        )
      }}
    </v-card-text>
    <div class="report-footer">
      <!-- SIGNATURE -->
      <v-card-text class="pa-0 ma-0 text-body-2 signature">
        {{ $t("Seal and signature of the employer / their legal representative.") }}
      </v-card-text>
      <!-- RECEIPT BOX -->
      <v-card-text class="pa-0 pa-1 text-body-2 ma-0 receipt">
        {{
          $t(
            "It is hereby certified that the current monthly payroll, including … insured persons, was received on … / … / … from the workshop with number …, contract line number …, through … floppy disk(s)."
          )
        }}

        <br />
        <v-card-text class="pa-0 ma-0 my-2 text-body-1 signature">
          {{ $t("Branch seal and signature.") }}
        </v-card-text>
      </v-card-text>
    </div>
  </v-container>
</template>

<script>
export default {
  name: "InsuranceSummaryReport",
  data() {
    return {
      workshopSalary: null,
    };
  },
  computed: {
    currencyLabel() {
      return this.$store?.state?.currency?.name || "";
    },
    salaryMonth() {
      return this.workshopSalary?.month || this.$route?.query?.month || null;
    },
    salaryYear() {
      return this.workshopSalary?.year || this.$route?.query?.year || null;
    },
    workshopCode() {
      return (
        this.workshopSalary?.workshop?.insurance_code ||
        this.$route?.query?.workshop_code ||
        this.$route?.query?.hrm_workshop_id ||
        null
      );
    },
    summaryRows() {
      const ws = this.workshopSalary;
      if (!ws) {
        return [];
      }
      const benefit = this.toNumber(ws.benefit);
      const applicable = this.toNumber(ws.applicable_insurance_value);
      const employee = this.toNumber(ws.employee_insurance);
      const company = this.toNumber(ws.company_insurance);
      const jobless = this.toNumber(ws.jobless_insurance);
      return [
        {
          label: this.$t(
            "Total wages and benefits subject to and not subject to insurance premium deductions"
          ),
          amount: benefit,
        },
        {
          label: this.$t("Total wages and benefits subject to insurance premium deductions"),
          amount: applicable,
        },
        {
          label: this.$t("Total paid benefits"),
          amount: benefit - applicable,
        },
        {
          label: this.$t("Total insurance premium – employees’ share"),
          amount: employee,
        },
        {
          label: this.$t("Total employer’s share of insurance premium for the month"),
          amount: company,
        },
        {
          label: this.$t("Total jobless’s share of insurance premium for the month"),
          amount: jobless,
        },
        {
          label: this.$t("Total monthly insurance premium for hazardous and arduous occupations"),
          amount: 0,
        },
        {
          label: this.$t(
            "Total insurance contributions, including social insurance and unemployment insurance"
          ),
          amount: employee + company + jobless,
          isTotal: true,
        },
      ];
    },
  },
  beforeMount() {
    this.getItems();
  },
  methods: {
    toNumber(v) {
      if (v === null || v === undefined || v === "") {
        return 0;
      }
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    },
    formatSummaryAmount(amount) {
      return this.$numberFormat(amount);
    },
    buildSummaryReportObject() {
      const ws = this.workshopSalary || {};
      return {
        items: this.$t("Payroll summary"),
        month: this.salaryMonth || "",
        year: this.salaryYear || "",
        workshopName: ws?.workshop?.name || ws?.workshop_name || "",
        workshopCode: this.workshopCode || "",
        branch: ws?.branch?.name || ws?.branch_name || "",
        header: this.$t(
          "This form relates to the monthly payroll/salaries and benefits of the workshop’s employees, and a summary of its status is presented below"
        ),
        root_items: this.summaryRows.map((row, index) => ({
          relationId: `summary_${index + 1}`,
          row: index + 1,
          label: row.label,
          amount: this.toNumber(row.amount),
          isTotal: !!row.isTotal,
          currency: this.currencyLabel || "",
        })),
        footer: this.$t(
          "Furthermore, it is hereby undertaken that the information stored on the diskette exactly corresponds to the contents stated above."
        ),
        boxfooter: this.$t(
          "It is hereby certified that the current monthly payroll, including … insured persons, was received on … / … / … from the workshop with number …, contract line number …, through … floppy disk(s)."
        ),
        signitureTop: this.$t(
          "Seal and signature of the employer / their legal representative."
        ),
        signitureBottom: this.$t("Branch seal and signature."),
      };
    },
    getItems() {
      var app = this;
      const id_ = app.$route.query.id;
      const workshopId = app.$route.query.hrm_workshop_id;
      app.$axios
        .get(app.$endpoint + "salary/" + id_, {
          params: workshopId ? { workshop: workshopId } : {},
        })
        .then((response) => {
          app.workshopSalary = response.data.workshop_salary || null;
          app.$emit("summeryListObject", app.buildSummaryReportObject());
        })
        .catch((err) => {
          app.serverErrorHandeler(err);
        })
        .finally(() => {
          app.$emit("initial-Loading-is", false);
          app.$emit("call-refresh");
        });
    },
  },
};
</script>

<style scoped>
.report-wrapper {
  direction: rtl;
  /* font-family: Tahoma, sans-serif; */
  padding: 20px;
  background: #fff;
  color: #444;
}

.title {
  text-align: center;
  font-size: 20px;
  font-weight: bold;
}

.subtitle {
  text-align: center;
  color: #777;
}

.workshop {
  text-align: center;
  color: #888;
}

.desc {
  text-align: center;
  font-size: 13px;
  color: #666;
  margin-bottom: 30px;
}

.summary-table {
  width: 100%;
  table-layout: auto;
  border-collapse: collapse;
  margin-bottom: 3px;
  font-size: 11px;
  font-family: IRANSans !important;
  min-width: 760px;
}

.summary-table th,
.summary-table td {
  border: 1px solid #ddd;
  padding: 1px 6px;
  font-size: 10px;
}

.summary-table th {
  background: #f5f5f5;
  text-align: right;
}

.summary-table td {
  text-align: left;
}

.summary-table .label-cell {
  width: auto;
  font-weight: 500;
}

.summary-table .amount-cell {
  width: 220px;
  text-align: center;
  white-space: nowrap;
}

.summary-table .currency-cell {
  width: 52px;
  text-align: center;
  white-space: nowrap;
}

.summary-table .summary-total-row th,
.summary-table .summary-total-row td {
  font-weight: 700;
}

.confirm {
  text-align: center;
  color: #777;
}

.signature {
  text-align: left;
  margin-bottom: 5px;
}

.receipt {
  border: 1px solid #ddd;
  padding: 10px;
  line-height: 2;
  font-size: 12px;
}

.report-footer {
  break-inside: avoid;
  page-break-inside: avoid;
}

.table-scroll-x {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

@media (max-width: 1024px) {
  .report-wrapper {
    padding: 12px;
  }

  .title {
    font-size: 16px;
    line-height: 1.8;
  }

  .subtitle,
  .desc,
  .confirm,
  .receipt {
    font-size: 12px;
  }

  .summary-table {
    min-width: 640px;
  }

  .summary-table th,
  .summary-table td {
    padding: 5px 6px;
    font-size: 9px;
  }

  .summary-table .amount-cell {
    width: 180px;
  }

  .summary-table .currency-cell {
    width: 46px;
  }
}

@media print {
  .report-wrapper {
    padding: 8px;
  }

  .desc {
    margin-bottom: 8px;
  }

  .summary-table th,
  .summary-table td {
    padding: 1px 4px;
    font-size: 9px;
  }

  .confirm {
    margin-top: 4px;
    margin-bottom: 4px;
  }

  .report-footer {
    margin-top: 2px;
    break-inside: avoid;
    page-break-inside: avoid;
  }

  .signature {
    margin-bottom: 2px;
  }

  .receipt {
    padding: 6px;
    line-height: 1.6;
    font-size: 11px;
  }
}
</style>
